import type { SpaceDerivationOutput } from '@toolproof-core/visualization/derivation/_contract/derivation';
import type { SpaceOverlayIntent, SpaceOverlayModel } from '@toolproof-core/visualization/runtime/_contract/overlay';

import { MINIMUM_POSITION_INTERVAL_MS } from '@/workflow-runner/_data/config/config';
import { requireConfig } from '@/workflow-runner/_pure/config/config';
import { buildPrimitiveOperationPlaybackStateRows } from '@/workflow-runner/_pure/interaction/primitiveOperationPlaybackStateRows';
import {
    clampPositionIndex,
    clampPositionIntervalMs,
} from '@/workflow-runner/_pure/model/playbackMath';
import { classifyReplayEdgeEventForPositionMove } from '@/workflow-runner/_pure/model/replay';
import {
    beginPendingReplayMove,
    completePrimitiveOperationStep,
    requestNextPrimitiveOperationStep,
    retreatPrimitiveOperationStep,
    settleReplayPosition,
} from '@/workflow-runner/_pure/model/replayState';
import { selectActiveToolStepFromTraceSnapshot } from '@/workflow-runner/_pure/model/traceSnapshotSelectors';
import { resolveReadyPrimitiveOperationReplay } from '@/workflow-runner/_pure/primitiveOperationRod';
import {
    createTraceSnapshotResolver,
    toSortedWorkflowTraces,
} from '@/workflow-runner/model/traceSnapshot';
import type { Config } from '@/workflow/_contract/config';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { TraceSnapshotResolver } from '@/workflow-runner/_contract/pipelineContext';
import type {
    RuntimeState,
} from '@/workflow-runner/_contract/runtime';
import type { TraceSnapshot } from '@/workflow-runner/_contract/traceSnapshot';

const SECTION_TRACE = 'trace-selector';
const SECTION_PLAYBACK = 'playback-controls';
const SECTION_STATUS = 'runner-status';
const SECTION_GROUPING = 'workflow-exploration-grouping';

const ACTION_PREV = 'replay-prev';
const ACTION_NEXT = 'replay-next';
const ACTION_RESET = 'replay-reset';
const ACTION_PLAY_PAUSE = 'replay-play-pause';

function resolvePendingPrimitiveOperationReplay(args: {
    cosmosData: CosmosDataModel;
    runtimeState: RuntimeState;
}) {
    const move = args.runtimeState.pendingReplayMove;
    if (move?.eventKind !== 'primitive-operation') return null;
    const resolver = createTraceSnapshotResolver();
    const replay = resolveReadyPrimitiveOperationReplay({
        fromSnapshot: resolver.deriveTraceSnapshotAtPosition({
            cosmosData: args.cosmosData,
            runtimeState: args.runtimeState,
            replayPositionIndex: move.fromPositionIndex,
        }),
        toSnapshot: resolver.deriveTraceSnapshotAtPosition({
            cosmosData: args.cosmosData,
            runtimeState: args.runtimeState,
            replayPositionIndex: move.toPositionIndex,
        }),
        toolMap: args.cosmosData.toolMap,
    });
    return replay === null ? null : { move, replay };
}

function beginPendingReplayMoveForOverlay(args: {
    runtimeState: RuntimeState;
    direction: 'forward' | 'reverse';
    fromPositionIndex: number;
    toPositionIndex: number;
    event: Parameters<typeof beginPendingReplayMove>[0]['event'];
}): RuntimeState {
    const { runtimeState, direction, fromPositionIndex, toPositionIndex, event } = args;
    return beginPendingReplayMove({
        runtimeState,
        direction,
        fromPositionIndex,
        toPositionIndex,
        event,
    });
}

function requestPositionMove(args: {
    cosmosData: CosmosDataModel;
    runtimeState: RuntimeState;
    traceSnapshotResolver: TraceSnapshotResolver;
    traceSnapshot: TraceSnapshot;
    requestedPosition: number;
    config: Config;
}): RuntimeState {
    const { cosmosData, runtimeState, traceSnapshot, requestedPosition } = args;
    const maxPosition = Math.max(traceSnapshot.timeline.replayPositionCount, 0);
    const currentPosition = traceSnapshot.position.appliedReplayPositionIndex;
    const nextPosition = clampPositionIndex(requestedPosition, maxPosition);

    if (runtimeState.pendingReplayMove) return runtimeState;
    if (nextPosition === currentPosition) return runtimeState;

    if (Math.abs(nextPosition - currentPosition) !== 1) {
        return settleReplayPosition({
            runtimeState,
            replayPositionIndex: nextPosition,
        });
    }

    const event = classifyReplayEdgeEventForPositionMove({
        fromSnapshot: args.traceSnapshotResolver.deriveTraceSnapshotAtPosition({
            cosmosData,
            runtimeState,
            replayPositionIndex: currentPosition,
        }),
        toSnapshot: args.traceSnapshotResolver.deriveTraceSnapshotAtPosition({
            cosmosData,
            runtimeState,
            replayPositionIndex: nextPosition,
        }),
        fromPosition: currentPosition,
        toPosition: nextPosition,
        config: requireConfig(args.config),
        toolMap: args.cosmosData.toolMap,
    });

    if (event.eventKind === 'none') {
        return settleReplayPosition({
            runtimeState,
            replayPositionIndex: nextPosition,
        });
    }

    return beginPendingReplayMoveForOverlay({
        runtimeState,
        direction: nextPosition > currentPosition ? 'forward' : 'reverse',
        fromPositionIndex: currentPosition,
        toPositionIndex: nextPosition,
        event,
    });
}

export function buildOverlayModel(args: {
    config: Config;
    runtimeState: RuntimeState;
    cosmosData: CosmosDataModel;
}): SpaceOverlayModel | undefined {
    if (args.config.adapterMode !== 'runner') return undefined;

    const { cosmosData } = args;
    const traceSnapshotResolver = createTraceSnapshotResolver();

    const sortedTraces = toSortedWorkflowTraces(cosmosData, args.runtimeState.ephemeralWorkflowTraces);

    if (sortedTraces.length === 0) return undefined;

    const traceSnapshot = traceSnapshotResolver.deriveTraceSnapshot({
        cosmosData,
        runtimeState: args.runtimeState,
    });

    const selectedHandle = traceSnapshot?.selectedTraceHandle ?? sortedTraces[0]?.handle ?? '';

    const positionIndex = traceSnapshot?.position.appliedReplayPositionIndex ?? 0;
    const positionCount = traceSnapshot?.timeline.replayPositionCount ?? 0;
    const pendingReplayMove = args.runtimeState.pendingReplayMove;
    const pendingPrimitiveOperationReplay = resolvePendingPrimitiveOperationReplay({
        cosmosData,
        runtimeState: args.runtimeState,
    });
    const isIdlePrimitiveOperation = pendingReplayMove?.eventKind === 'primitive-operation'
        && pendingReplayMove.arithmeticProgress.pendingStepIndex === null;
    const isTransitioning = pendingReplayMove !== null && !isIdlePrimitiveOperation;
    const activePrimitiveStepIndex = pendingPrimitiveOperationReplay === null
        ? undefined
        : pendingPrimitiveOperationReplay.move.arithmeticProgress.pendingStepIndex
            ?? pendingPrimitiveOperationReplay.move.arithmeticProgress.committedStepIndex;
    const activePrimitiveStep = activePrimitiveStepIndex === undefined
        ? undefined
        : pendingPrimitiveOperationReplay?.replay.resolution.steps[activePrimitiveStepIndex];
    const primitiveStepCount = pendingPrimitiveOperationReplay?.replay.resolution.steps.length;
    const primitiveOperationTitle = pendingPrimitiveOperationReplay?.replay.resolution.descriptor.title;
    const activeToolStep = selectActiveToolStepFromTraceSnapshot(traceSnapshot);
    const replayPhases: Record<NonNullable<RuntimeState['pendingReplayMove']>['eventKind'], string> = {
        'tool-execution': 'Executing tool',
        'primitive-operation': 'Processing operation',
        loop_continuation: 'Expanding loop',
        branch_exit_rebind: 'Returning from branch',
    };
    const phase = pendingReplayMove ? replayPhases[pendingReplayMove.eventKind]
        : positionIndex === positionCount ? 'Complete' : 'Ready';
    const operationName = primitiveOperationTitle
        ?? (activeToolStep ? cosmosData.toolMap.get(activeToolStep.toolHandle)?.name : undefined);

    return {
        overlayId: 'workflow-runner',
        title: 'Workflow Runner',
        sections: [
            ...(!args.runtimeState.episode ? [{
                sectionKind: 'select' as const,
                sectionId: SECTION_GROUPING,
                label: 'Grouping toruses',
                selectedValue: args.runtimeState.explorationGrouping ?? 'configured',
                enabled: pendingReplayMove === null && !args.runtimeState.isPlaying,
                options: [
                    { value: 'configured', label: 'Configured default' },
                    { value: 'none', label: 'Off' },
                    { value: 'cases', label: 'Cases' },
                    { value: 'macroSteps', label: 'MacroStep' },
                ],
            }] : []),
            {
                sectionKind: 'select',
                sectionId: SECTION_TRACE,
                label: 'Workflow Trace',
                selectedValue: selectedHandle,
                options: sortedTraces.map((trace) => ({
                    value: trace.handle,
                    label: trace.handle,
                })),
            },
            {
                sectionKind: 'actions',
                sectionId: SECTION_PLAYBACK,
                label: 'Replay',
                description: positionCount <= 0 ? 'No replay positions'
                    : pendingReplayMove === null && positionIndex >= positionCount ? 'Complete' : 'Ready',
                actions: [
                    {
                        actionId: ACTION_PLAY_PAUSE,
                        playbackRole: 'toggle',
                        label: args.runtimeState.isPlaying ? 'Pause' : 'Play',
                        enabled: args.runtimeState.isPlaying || pendingReplayMove !== null || positionIndex < positionCount,
                        isActive: args.runtimeState.isPlaying,
                    },
                    {
                        actionId: ACTION_RESET,
                        playbackRole: 'reset',
                        label: 'Reset',
                        enabled: positionIndex > 0 || pendingReplayMove !== null,
                    },
                    {
                        actionId: ACTION_PREV,
                        playbackRole: 'step',
                        label: 'Prev',
                        enabled: (positionIndex > 0 || isIdlePrimitiveOperation) && !isTransitioning && !args.runtimeState.isPlaying,
                    },
                    {
                        actionId: ACTION_NEXT,
                        playbackRole: 'step',
                        label: 'Next',
                        enabled: (isIdlePrimitiveOperation
                            ? (activePrimitiveStepIndex ?? 0) < (primitiveStepCount ?? 0) - 1
                            : positionIndex < positionCount) && !isTransitioning && !args.runtimeState.isPlaying,
                    },
                ],
            },
            {
                sectionKind: 'details',
                sectionId: SECTION_STATUS,
                label: 'State',
                rows: [
                    { key: 'Position', value: positionCount > 0 ? `${positionIndex} / ${positionCount}` : '—' },
                    ...(operationName ? [{ key: 'Operation', value: operationName }] : []),
                    { key: 'Phase', value: phase },
                    ...buildPrimitiveOperationPlaybackStateRows({
                        stepIndex: activePrimitiveStepIndex,
                        stepCount: primitiveStepCount,
                        step: activePrimitiveStep,
                    }),
                ],
            },
        ],
    };
}

export function reduceOverlayIntent(args: {
    config: Config;
    runtimeState: RuntimeState;
    intent: SpaceOverlayIntent;
    cosmosData: CosmosDataModel;
}): RuntimeState {
    const { config, intent } = args;
    if (config.adapterMode !== 'runner') return args.runtimeState;

    if (intent.overlayIntentKind === 'select-changed' && intent.sectionId === SECTION_GROUPING) {
        if (args.runtimeState.episode || args.runtimeState.pendingReplayMove || args.runtimeState.isPlaying) return args.runtimeState;
        if (!['configured', 'none', 'cases', 'macroSteps'].includes(intent.value)) {
            throw new Error(`Unknown exploration grouping '${intent.value}'.`);
        }
        return { ...args.runtimeState, explorationGrouping: intent.value === 'configured' ? undefined
            : intent.value === 'cases' ? 'cases' : intent.value === 'macroSteps' ? 'macroSteps' : 'none' };
    }

    const traceSnapshotResolver = createTraceSnapshotResolver();

    const traceSnapshot = traceSnapshotResolver.deriveTraceSnapshot({
        cosmosData: args.cosmosData,
        runtimeState: args.runtimeState,
    });

    const currentTraceSelection = args.runtimeState.traceSelection;

    if (intent.overlayIntentKind === 'select-changed' && intent.sectionId === SECTION_TRACE) {
        if (args.runtimeState.pendingReplayMove !== null || args.runtimeState.isPlaying) return args.runtimeState;
        return {
            ...args.runtimeState,
            traceSelection: {
                ...currentTraceSelection,
                workflowTraceSelectionKind: 'by-handle',
                workflowTraceHandle: intent.value,
                replayPositionIndex: 0,
            },
            playbackAdvanceMode: 'manual',
            isPlaying: false,
            accumulatedMs: 0,
            pendingReplayMove: null,
        };
    }

    if (intent.overlayIntentKind === 'action-triggered') {
        if (['camera-overview', 'camera-focus-tool-step', 'camera-free'].includes(intent.actionId)) return args.runtimeState;
        const pendingReplayMove = args.runtimeState.pendingReplayMove;
        const isIdlePrimitiveOperation = pendingReplayMove?.eventKind === 'primitive-operation'
            && pendingReplayMove.arithmeticProgress.pendingStepIndex === null;
        if (intent.actionId === ACTION_RESET) {
            return settleReplayPosition({
                runtimeState: { ...args.runtimeState, playbackAdvanceMode: 'manual', isPlaying: false, accumulatedMs: 0 },
                replayPositionIndex: 0,
            });
        }
        if (pendingReplayMove !== null && !isIdlePrimitiveOperation) {
            return args.runtimeState;
        }

        const activeRuntimeState = args.runtimeState;

        if (intent.actionId === ACTION_PLAY_PAUSE) {
            if (isIdlePrimitiveOperation) {
                return requestNextPrimitiveOperationStep({
                    ...activeRuntimeState,
                    playbackAdvanceMode: 'automatic',
                    isPlaying: true,
                });
            }
            if (!activeRuntimeState.isPlaying
                && (traceSnapshot?.position.appliedReplayPositionIndex ?? 0) >= (traceSnapshot?.timeline.replayPositionCount ?? 0)) {
                return activeRuntimeState;
            }
            const nextIsPlaying = !activeRuntimeState.isPlaying;
            return {
                ...activeRuntimeState,
                playbackAdvanceMode: 'automatic',
                isPlaying: nextIsPlaying,
            };
        }
        if (activeRuntimeState.isPlaying) return activeRuntimeState;

        const currentPosition = traceSnapshot?.position.appliedReplayPositionIndex ?? 0;
        const maxPosition = Math.max(traceSnapshot?.timeline.replayPositionCount ?? 0, 0);

        if (isIdlePrimitiveOperation) {
            const manualRuntimeState: RuntimeState = {
                ...activeRuntimeState,
                playbackAdvanceMode: 'manual',
                isPlaying: false,
            };
            if (intent.actionId === ACTION_NEXT) {
                const pendingReplay = resolvePendingPrimitiveOperationReplay({
                    cosmosData: args.cosmosData,
                    runtimeState: manualRuntimeState,
                });
                if (!pendingReplay) return manualRuntimeState;
                const lastStepIndex = pendingReplay.replay.resolution.steps.length - 1;
                return pendingReplay.move.arithmeticProgress.committedStepIndex < lastStepIndex
                    ? requestNextPrimitiveOperationStep(manualRuntimeState)
                    : settleReplayPosition({
                        runtimeState: manualRuntimeState,
                        replayPositionIndex: pendingReplay.move.toPositionIndex,
                    });
            }
            if (intent.actionId === ACTION_PREV) {
                return retreatPrimitiveOperationStep(manualRuntimeState);
            }
            if (intent.actionId === ACTION_RESET) {
                return settleReplayPosition({ runtimeState: manualRuntimeState, replayPositionIndex: 0 });
            }
            return manualRuntimeState;
        }

        let nextPosition = currentPosition;
        if (intent.actionId === ACTION_PREV) {
            nextPosition = Math.max(currentPosition - 1, 0);
        } else if (intent.actionId === ACTION_NEXT) {
            nextPosition = Math.min(currentPosition + 1, maxPosition);
        } else if (intent.actionId === ACTION_RESET) {
            nextPosition = 0;
        }

        const manualRuntimeState: RuntimeState = {
            ...activeRuntimeState,
            playbackAdvanceMode: 'manual',
            isPlaying: false,
        };

        if (!traceSnapshot) return manualRuntimeState;
        if (nextPosition === currentPosition) return manualRuntimeState;

        return requestPositionMove({
            cosmosData: args.cosmosData,
            runtimeState: manualRuntimeState,
            traceSnapshotResolver,
            traceSnapshot,
            requestedPosition: nextPosition,
            config,
        });
    }

    return args.runtimeState;
}

export function getRuntimeTickIntervalMs(args: {
    config: Config;
    runtimeState: RuntimeState;
    cosmosData: CosmosDataModel;
}): number | undefined {
    void args.cosmosData;
    if (args.config.adapterMode !== 'runner') return undefined;
    if (args.runtimeState.pendingReplayMove) return undefined;
    if (args.runtimeState.playbackAdvanceMode !== 'automatic') return undefined;
    if (!args.runtimeState.isPlaying) return undefined;
    return clampPositionIntervalMs(args.runtimeState.positionIntervalMs, MINIMUM_POSITION_INTERVAL_MS);
}

export function reduceRuntimeTick(args: {
    config: Config;
    runtimeState: RuntimeState;
    cosmosData: CosmosDataModel;
    deltaMs: number;
}): RuntimeState {
    if (args.config.adapterMode !== 'runner') return args.runtimeState;

    const traceSnapshotResolver = createTraceSnapshotResolver();

    const traceSnapshot = traceSnapshotResolver.deriveTraceSnapshot({
        cosmosData: args.cosmosData,
        runtimeState: args.runtimeState,
    });

    const replayPositionCount = traceSnapshot?.timeline.replayPositionCount ?? 0;
    const maxPosition = Math.max(replayPositionCount, 0);
    const currentPosition = traceSnapshot?.position.appliedReplayPositionIndex ?? 0;
    if (replayPositionCount <= 0) {
        return {
            ...args.runtimeState,
            isPlaying: false,
            accumulatedMs: 0,
            pendingReplayMove: null,
        };
    }

    if (args.runtimeState.pendingReplayMove) return args.runtimeState;

    if (args.runtimeState.playbackAdvanceMode !== 'automatic') return args.runtimeState;
    if (!args.runtimeState.isPlaying) return args.runtimeState;

    const intervalMs = clampPositionIntervalMs(args.runtimeState.positionIntervalMs, MINIMUM_POSITION_INTERVAL_MS);
    const nextAccumulatedMs = args.runtimeState.accumulatedMs + Math.max(args.deltaMs, 0);
    if (nextAccumulatedMs < intervalMs) {
        return {
            ...args.runtimeState,
            accumulatedMs: nextAccumulatedMs,
        };
    }

    if (!traceSnapshot) {
        return {
            ...args.runtimeState,
            isPlaying: false,
            accumulatedMs: 0,
        };
    }

    const steppedState = requestPositionMove({
        cosmosData: args.cosmosData,
        runtimeState: {
            ...args.runtimeState,
            accumulatedMs: nextAccumulatedMs - intervalMs,
        },
        traceSnapshotResolver,
        traceSnapshot,
        requestedPosition: Math.min(currentPosition + 1, maxPosition),
        config: args.config,
    });

    const steppedPosition = clampPositionIndex(
        steppedState.traceSelection.replayPositionIndex ?? currentPosition,
        maxPosition
    );
    const reachedEnd = steppedPosition >= maxPosition;
    if (reachedEnd) {
        return {
            ...steppedState,
            isPlaying: false,
            accumulatedMs: 0,
        };
    }

    return steppedState;
}

// Descriptor-level adapter wrappers for generic hook signatures
export function buildRunnerOverlayModel(args: {
    config: Config;
    runtimeState: RuntimeState;
    cosmosData: CosmosDataModel;
    derivationOutput: SpaceDerivationOutput;
}): SpaceOverlayModel | undefined {
    return buildOverlayModel({
        config: args.config,
        runtimeState: args.runtimeState,
        cosmosData: args.cosmosData,
    });
}

export function reduceRunnerOverlayIntent(args: {
    config: Config;
    runtimeState: RuntimeState;
    intent: SpaceOverlayIntent;
    cosmosData: CosmosDataModel;
}): RuntimeState {
    return reduceOverlayIntent({
        config: args.config,
        runtimeState: args.runtimeState,
        intent: args.intent,
        cosmosData: args.cosmosData,
    });
}

export function reduceDerivationTransitionComplete(args: {
    config: Config;
    runtimeState: RuntimeState;
    cosmosData: CosmosDataModel;
}): RuntimeState {
    void args.config;
    const pendingReplayMove = args.runtimeState.pendingReplayMove;
    if (!pendingReplayMove) return args.runtimeState;

    if (pendingReplayMove.eventKind === 'primitive-operation') {
        const pendingReplay = resolvePendingPrimitiveOperationReplay({
            cosmosData: args.cosmosData,
            runtimeState: args.runtimeState,
        });
        if (!pendingReplay || pendingReplayMove.arithmeticProgress.pendingStepIndex === null) {
            return args.runtimeState;
        }
        const lastStepIndex = pendingReplay.replay.resolution.steps.length - 1;
        const targetSnapshot = createTraceSnapshotResolver().deriveTraceSnapshotAtPosition({
            cosmosData: args.cosmosData,
            runtimeState: args.runtimeState,
            replayPositionIndex: pendingReplayMove.toPositionIndex,
        });
        return completePrimitiveOperationStep({
            runtimeState: args.runtimeState,
            lastStepIndex,
            replayPositionCount: targetSnapshot?.timeline.replayPositionCount ?? Number.POSITIVE_INFINITY,
            manualInspection: pendingReplay.replay.resolution.descriptor.descriptorKind === 'natural-less-than'
                || pendingReplay.replay.resolution.descriptor.descriptorKind === 'boolean-algebra',
        });
    }

    const settled = settleReplayPosition({
        runtimeState: args.runtimeState,
        replayPositionIndex: pendingReplayMove.toPositionIndex,
    });
    const snapshot = createTraceSnapshotResolver().deriveTraceSnapshot({
        cosmosData: args.cosmosData, runtimeState: settled,
    });
    return snapshot !== undefined && pendingReplayMove.toPositionIndex >= snapshot.timeline.replayPositionCount
        ? { ...settled, isPlaying: false, accumulatedMs: 0 }
        : settled;
}
